import {
    FORMS_INVITATION_ERROR,
    FORMS_INVITATION_MARK_NOTIFICATION_SHOWN,
    FORMS_INVITATION_SET_DATA,
    FORMS_INVITATION_SET_LOADING,
    FORMS_INVITATION_SET_RESPONSE_SET_ID,
    FORMS_INVITATION_SET_VIEW,
    FORMS_INVITATION_SUCCESS,
    FORMS_INVITATION_UPDATE_ANSWER,
    FormsInvitationAction
} from 'actions/formsInvitation/FormsInvitationActionsTypes';
import {
    FormsInvitationState,
    formsInvitationInitialState
} from 'reducers/formsInvitation/FormsInvitationState';

export const formsInvitationReducer = (
    state: FormsInvitationState = formsInvitationInitialState,
    action: FormsInvitationAction
): FormsInvitationState => {
    switch (action.type) {
        case FORMS_INVITATION_SET_LOADING:
            return {
                ...state,
                loading: action.payload.loading
            };

        case FORMS_INVITATION_SET_DATA: {
            // Initialize answers and responseIds from responses
            const initialAnswers: Record<string, { [questionId: number]: string | string[] }> = {};
            const initialResponseIds: Record<string, { [questionId: number]: number }> = {};

            // Customer forms
            action.payload.data.customer.forms.forEach(form => {
                initialAnswers[form.id] = {};
                initialResponseIds[form.id] = {};

                form.questions.forEach(question => {
                    if (question.response?.answer) {
                        initialAnswers[form.id][question.id] = question.response.answer;
                        initialResponseIds[form.id][question.id] = question.response.id; // ← Guardar ResponseData.id
                    }
                });
            });

            // Pet forms: store answers under composite key `${formId}_${petId}` to avoid collisions
            action.payload.data.pets.forEach(pet => {
                pet.forms.forEach(form => {
                    const compositeKey = `${form.id}_${pet.id}`;
                    if (!initialAnswers[compositeKey]) {
                        initialAnswers[compositeKey] = {};
                        initialResponseIds[compositeKey] = {};
                    }

                    form.questions.forEach(question => {
                        if (question.response?.answer) {
                            initialAnswers[compositeKey][question.id] = question.response.answer;
                            initialResponseIds[compositeKey][question.id] = question.response.id;
                        }
                    });
                });
            });

            return {
                ...state,
                data: action.payload.data,
                answers: initialAnswers,
                responseIds: initialResponseIds // ← Nuevo campo
            };
        }

        case FORMS_INVITATION_SET_VIEW:
            return {
                ...state,
                currentView: action.payload.view
            };

        case FORMS_INVITATION_UPDATE_ANSWER: {
            // If subjectId provided, store under composite key
            type UpdateAnswerPayload = {
                formId: number;
                questionId: number;
                answer: string | string[];
                subjectId?: number;
            };

            const payload = action.payload as UpdateAnswerPayload;
            const subjectId = payload.subjectId;
            const key = subjectId ? `${payload.formId}_${subjectId}` : String(payload.formId);

            const prevForKey = state.answers[key] || {};

            return {
                ...state,
                answers: {
                    ...state.answers,
                    [key]: {
                        ...prevForKey,
                        [payload.questionId]: payload.answer
                    }
                }
            };
        }

        case FORMS_INVITATION_SET_RESPONSE_SET_ID: {
            type SetResponseSetPayload = {
                formId: number;
                responseSetId: number;
                subjectId?: number;
            };

            const payload = action.payload as SetResponseSetPayload;
            const key = payload.subjectId
                ? `${payload.formId}_${payload.subjectId}`
                : String(payload.formId);

            return {
                ...state,
                formResponseSetIds: {
                    ...state.formResponseSetIds,
                    [key]: payload.responseSetId
                }
            };
        }

        case FORMS_INVITATION_ERROR: {
            type ErrorActionPayload = {
                code?: number;
                message?: string;
            };

            const payload = action.payload as ErrorActionPayload | undefined;

            return {
                ...state,
                loading: false,
                error: payload
                    ? {
                          code: payload.code || 500,
                          message: payload.message || 'An error occurred'
                      }
                    : null
            };
        }

        case FORMS_INVITATION_SUCCESS:
            return {
                ...state,
                loading: false
            };

        case FORMS_INVITATION_MARK_NOTIFICATION_SHOWN: {
            const { notificationType } = action.payload as {
                notificationType: 'addQuestion' | 'addPet';
            };
            if (notificationType === 'addQuestion') {
                return {
                    ...state,
                    notifyAddQuestionShown: true
                };
            } else if (notificationType === 'addPet') {
                return {
                    ...state,
                    notifyAddPetShown: true
                };
            }
            return state;
        }

        default:
            return state;
    }
};
